import { useEffect } from "react";
import { ConfigModel, selectConfig } from "../store/features/configSlice";
import { useAppSelector } from "../store/hooks";
import { RecaptchaBaseUrl } from "../config";

declare global {
  interface Window {
    grecaptcha: any;
  }
}

//#region Assessment request model
interface AssessmentRequestModel {
  event: AssessmentRequestEvent;
}

interface AssessmentRequestEvent {
  token: string;
  siteKey: string;
  expectedAction?: string;
}
//#endregion

//#region Assessment response model

interface AssessmentResponseModel {
  name: string;
  event: AssessmentResponseEvent;
  riskAnalysis: RiskAnalysisModel;
  tokenProperties: TokenProperties;
}

interface RiskAnalysisModel {
  reasons: any[];
  score: number;
}

interface AssessmentResponseEvent {
  token: string;
  siteKey: string;
  userAgent: string;
  userIpAddress: string;
  expectedAction: string;
}

interface TokenProperties {
  valid: boolean;
  invalidReason: string;
  hostname: string;
  action: string;
  createTime: string;
}

//#endregion

interface Props {
  notARobot: boolean;
  action?: string;
  onUpdateToken: (ev: boolean) => void;
}

const validateToken = (
  configState: ConfigModel,
  token: string,
  action?: string,
  minScore: number = 0.8
): Promise<boolean> => {
  return new Promise<boolean>((resolve, reject) => {
    if (token != null && token.length > 0) {
      const url = `${RecaptchaBaseUrl}/${configState.googleprojectid}/assessments?key=${configState.googleapikey}`;

      const data: AssessmentRequestModel = {
        event: {
          expectedAction: action,
          siteKey: configState.googlesitekey || "",
          token,
        },
      };

      fetch(url, { method: "POST", body: JSON.stringify(data) }).then(
        (response) => {
          response.text().then((result) => {
            const parsedAssessment: AssessmentResponseModel =
              JSON.parse(result);

            resolve(
              parsedAssessment.event.expectedAction === action &&
                parsedAssessment.riskAnalysis.score >= minScore
            );
          });
        }
      );
    } else {
      resolve(false);
    }
  });
};

export default function GoogleRecaptcha(props: Props) {
  const configState: ConfigModel = useAppSelector(selectConfig);

  useEffect(() => {
    const includeScript = () => {
      if (window.grecaptcha == null) {
        const scriptElement = document.createElement("script");
        scriptElement.src =
          "https://www.google.com/recaptcha/enterprise.js?render=explicit";
        scriptElement.onload = () => {
          renderButton();
        };

        document.body.appendChild(scriptElement);
      } else {
        setTimeout(() => {
          renderButton();
        }, 0);
      }
    };

    const renderButton = () => {
      if (document.getElementById("googleRecapchaCheckbox")?.innerHTML === "") {
        window.grecaptcha.enterprise.ready(async () => {
          window.grecaptcha.enterprise.render("googleRecapchaCheckbox", {
            sitekey: configState.googlesitekey,
            action: props.action,
            callback: async (responseToken: string) => {
              const isValidToken: boolean = await validateToken(
                configState,
                responseToken,
                props.action
              );
              props.onUpdateToken(isValidToken);
            },
          });
        });
      }
    };

    if (configState != null && configState.googleapikey != null) {
      includeScript();
    }
  }, [props, configState]);

  return (
    <>
      <div id="googleRecapchaCheckbox"></div>
    </>
  );
}
